/**
 * Phase 4 — Service-worker registration helper.
 *
 * Only registers on production builds (CRA convention) and on the web bundle
 * (Capacitor native bundles already manage their own asset loading).
 *
 * Exposes:
 *   register()           — call once from index.js
 *   unregister()         — for debugging
 *   onUpdateAvailable(cb)— pass a callback that gets a fn `applyUpdate()`
 *                          which sends SKIP_WAITING + reloads.
 */
import { IS_WEB } from "../appConfig";

let _updateCallbacks = [];

export function onUpdateAvailable(cb) {
  _updateCallbacks.push(cb);
  return () => {
    _updateCallbacks = _updateCallbacks.filter((f) => f !== cb);
  };
}

function _fireUpdateAvailable(reg) {
  const applyUpdate = () => {
    if (reg.waiting) {
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    // Reload once the new SW takes control.
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  };
  _updateCallbacks.forEach((cb) => {
    try { cb(applyUpdate); } catch { /* ignore */ }
  });
}

function _trackUpdates(reg) {
  if (!reg) return;
  if (reg.waiting) _fireUpdateAvailable(reg);
  reg.addEventListener("updatefound", () => {
    const sw = reg.installing;
    if (!sw) return;
    sw.addEventListener("statechange", () => {
      if (sw.state === "installed" && navigator.serviceWorker.controller) {
        _fireUpdateAvailable(reg);
      }
    });
  });
}

export function register() {
  // Web bundle only — native Capacitor apps don't need an SW.
  if (!IS_WEB) return;
  if (!("serviceWorker" in navigator)) return;
  if (process.env.NODE_ENV !== "production") return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        _trackUpdates(reg);
        // Re-check for new SW once an hour while the tab lives.
        setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[sw] registration failed", err);
      });
  });
}

export function unregister() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
}
